import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request } from '../api/client';

const DEFAULT_TELEFONE = '5500000000001';

interface PlaygroundCompra {
  produto: string;
  status: string;
  valorCentavos: number;
  tokenValidacao: string | null;
  compradaEm: string | null;
}

interface PlaygroundMensagem {
  id: string;
  autor: string;
  conteudo: string;
  intent: string | null;
  enviadaEm: string;
}

interface PlaygroundState {
  cliente: {
    id: string;
    telefone: string;
    primeiroNome: string | null;
    email: string | null;
    creditosTiragem: number;
    primeiraCompra: string | null;
    diasDesdeCompra: number | null;
    acessoMapaManual: boolean;
    perguntaTiragemPendente: string | null;
  };
  conversa: {
    validada: boolean;
    pausada: boolean;
    intervencaoHumana: boolean;
    flagIntervencao: string | null;
  };
  compras: PlaygroundCompra[];
  tokenAtivo: string | null;
  mensagens: PlaygroundMensagem[];
}

export function PlaygroundPage(): JSX.Element {
  const [telefone, setTelefone] = useState(DEFAULT_TELEFONE);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery<PlaygroundState>({
    queryKey: ['playground-state', telefone],
    queryFn: () =>
      request<PlaygroundState>(`/admin/playground/state?telefone=${encodeURIComponent(telefone)}`),
    enabled: !!telefone,
  });

  return (
    <section aria-labelledby="pg-heading" className="space-y-6">
      <header>
        <h1 id="pg-heading" className="h1">
          Playground da Sara
        </h1>
        <p className="muted text-sm mt-1">
          Sandbox pra testar a Sara como se fosse um WhatsApp real, sem mandar mensagem pra ninguém.
          Configure o cenário (dias desde compra, créditos, upsell, email), mande mensagens, veja a
          resposta. Reset a qualquer momento.
        </p>
      </header>

      <div className="card">
        <label htmlFor="pg-telefone" className="label">
          Telefone de teste (precisa começar com <code className="px-1">5500</code> — proteção
          contra mexer em cliente real)
        </label>
        <input
          id="pg-telefone"
          type="tel"
          inputMode="tel"
          className="input"
          value={telefone}
          onChange={(e) => setTelefone(e.target.value.replace(/\D/g, ''))}
        />
        <p className="text-xs text-sara-text-subtle mt-1.5">
          Default <code>{DEFAULT_TELEFONE}</code>. Pode usar variações tipo{' '}
          <code>5500000000002</code> pra testar 2 cenários em paralelo.
        </p>
      </div>

      {isLoading && <div className="card muted text-sm">Carregando estado…</div>}
      {isError && (
        <div className="card border-sara-danger" role="alert">
          <p className="text-sara-danger text-sm">Erro carregando playground.</p>
        </div>
      )}

      {data && (
        <div className="grid lg:grid-cols-[1fr_400px] gap-6 items-start">
          <ChatPanel
            state={data}
            telefone={telefone}
            onAfterSend={() => {
              void queryClient.invalidateQueries({ queryKey: ['playground-state', telefone] });
            }}
            onReset={() => {
              void refetch();
            }}
          />
          <ConfigPanel
            state={data}
            telefone={telefone}
            onAfterChange={() => {
              void queryClient.invalidateQueries({ queryKey: ['playground-state', telefone] });
            }}
          />
        </div>
      )}
    </section>
  );
}

function ChatPanel({
  state,
  telefone,
  onAfterSend,
  onReset,
}: {
  state: PlaygroundState;
  telefone: string;
  onAfterSend: () => void;
  onReset: () => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [state.mensagens.length]);

  const send = useMutation({
    mutationFn: (body: { text: string }) =>
      request<{ balloons: Array<{ text: string }>; intent: string | null; tags: string[] }>(
        '/admin/playground/send',
        { method: 'POST', body: { telefone, text: body.text } },
      ),
    onSuccess: () => {
      setText('');
      onAfterSend();
    },
  });

  const reset = useMutation({
    mutationFn: () =>
      request<{ ok: true }>('/admin/playground/reset', {
        method: 'POST',
        body: { telefone },
      }),
    onSuccess: () => {
      onReset();
    },
  });

  function handleSend(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    send.mutate({ text: trimmed });
  }

  return (
    <article aria-labelledby="chat-heading" className="card flex flex-col h-[700px]">
      <div className="flex items-center justify-between pb-3 border-b border-sara-border">
        <h2 id="chat-heading" className="h2">
          Chat
        </h2>
        <button
          type="button"
          className="btn-danger text-xs"
          onClick={() => {
            if (confirm('Apagar TODA a conversa (msgs, compras, créditos)?')) {
              reset.mutate();
            }
          }}
          disabled={reset.isPending}
          aria-busy={reset.isPending}
        >
          {reset.isPending ? 'Resetando…' : 'Resetar tudo'}
        </button>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto py-4 space-y-3"
        aria-label="Histórico de mensagens"
      >
        {state.mensagens.length === 0 ? (
          <p className="text-sara-text-subtle text-sm text-center italic mt-8">
            Manda uma mensagem pra começar. A Sara vai responder como se fosse pelo WhatsApp.
          </p>
        ) : (
          state.mensagens.map((m) => <MessageBubble key={m.id} msg={m} />)
        )}
        {send.isPending && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-sara-bg border border-sara-border px-4 py-3 text-sm muted italic">
              Sara digitando…
            </div>
          </div>
        )}
        {send.isError && (
          <div className="card border-sara-danger text-sm" role="alert">
            <p className="text-sara-danger">
              {send.error instanceof ApiError ? send.error.message : 'Erro ao enviar.'}
            </p>
          </div>
        )}
      </div>

      <form onSubmit={handleSend} className="pt-3 border-t border-sara-border flex gap-2">
        <input
          type="text"
          className="input flex-1"
          placeholder="Digita aqui como cliente…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={send.isPending}
        />
        <button
          type="submit"
          className="btn-primary"
          disabled={send.isPending || !text.trim()}
          aria-busy={send.isPending}
        >
          {send.isPending ? '…' : 'Enviar'}
        </button>
      </form>

      <div className="mt-2 flex flex-wrap gap-2">
        {state.tokenAtivo && (
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setText(state.tokenAtivo!)}
            title="Cola o token de validação no input"
          >
            Usar token: <code className="ml-1">{state.tokenAtivo}</code>
          </button>
        )}
        {state.cliente.email && (
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setText(state.cliente.email!)}
            title="Cola o email no input pra liberar via email"
          >
            Usar email: <code className="ml-1">{state.cliente.email}</code>
          </button>
        )}
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => setText('LIBERAR-TESTE')}
          title="Código de simulação de pagamento (cria Compra + crédito)"
        >
          Simular pix: <code className="ml-1">LIBERAR-TESTE</code>
        </button>
      </div>
    </article>
  );
}

function MessageBubble({ msg }: { msg: PlaygroundMensagem }): JSX.Element {
  const isCliente = msg.autor === 'cliente';
  return (
    <div className={`flex ${isCliente ? 'justify-end' : 'justify-start'}`}>
      <div
        className={[
          'max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words',
          isCliente
            ? 'bg-sara-accent text-sara-accent-fg rounded-br-sm'
            : 'bg-sara-bg border border-sara-border text-sara-text rounded-bl-sm',
        ].join(' ')}
      >
        {msg.conteudo}
        {msg.intent && !isCliente && (
          <div className="mt-1.5 text-[10px] uppercase tracking-wider opacity-60">
            intent: {msg.intent}
          </div>
        )}
      </div>
    </div>
  );
}

function ConfigPanel({
  state,
  telefone,
  onAfterChange,
}: {
  state: PlaygroundState;
  telefone: string;
  onAfterChange: () => void;
}): JSX.Element {
  const [nome, setNome] = useState(state.cliente.primeiroNome ?? '');
  const [email, setEmail] = useState(state.cliente.email ?? '');
  const [diasDesdeCompra, setDiasDesdeCompra] = useState<string>(
    state.cliente.diasDesdeCompra?.toString() ?? '',
  );
  const [creditos, setCreditos] = useState<string>(state.cliente.creditosTiragem.toString());
  const [temUpsell, setTemUpsell] = useState(
    state.compras.some((c) => c.produto === 'upsell1' && c.status === 'aprovada'),
  );
  const [acessoMapa, setAcessoMapa] = useState(state.cliente.acessoMapaManual);
  const [validada, setValidada] = useState(state.conversa.validada);

  useEffect(() => {
    setNome(state.cliente.primeiroNome ?? '');
    setEmail(state.cliente.email ?? '');
    setDiasDesdeCompra(state.cliente.diasDesdeCompra?.toString() ?? '');
    setCreditos(state.cliente.creditosTiragem.toString());
    setTemUpsell(state.compras.some((c) => c.produto === 'upsell1' && c.status === 'aprovada'));
    setAcessoMapa(state.cliente.acessoMapaManual);
    setValidada(state.conversa.validada);
  }, [state]);

  const apply = useMutation({
    mutationFn: () =>
      request<PlaygroundState>('/admin/playground/setup', {
        method: 'POST',
        body: {
          telefone,
          primeiroNome: nome,
          email: email || undefined,
          diasDesdeCompra: diasDesdeCompra ? Number(diasDesdeCompra) : undefined,
          creditosTiragem: creditos !== '' ? Number(creditos) : undefined,
          temUpsell,
          acessoMapaManual: acessoMapa,
          validada,
        },
      }),
    onSuccess: () => {
      onAfterChange();
    },
  });

  return (
    <aside aria-labelledby="config-heading" className="card space-y-5 sticky top-4">
      <div>
        <h2 id="config-heading" className="h2">
          Configurar cenário
        </h2>
        <p className="muted text-xs mt-1">
          Mudanças aqui afetam o estado do cliente no DB. Aperta "Aplicar" e manda mensagem pra ver
          a Sara reagir nessa condição.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="label" htmlFor="pg-nome">
            Nome
          </label>
          <input
            id="pg-nome"
            className="input"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="ex: Thais"
          />
        </div>

        <div>
          <label className="label" htmlFor="pg-email">
            Email (pra liberação por email)
          </label>
          <input
            id="pg-email"
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="cliente@example.com"
          />
          <p className="text-xs text-sara-text-subtle mt-1">
            Quando você "mandar" esse email no chat, a Sara busca uma Compra aprovada pra ele e
            libera. Mock criado se você marcar "Tem compra Front" abaixo.
          </p>
        </div>

        <div>
          <label className="label" htmlFor="pg-dias">
            Dias desde primeira compra
          </label>
          <input
            id="pg-dias"
            type="number"
            className="input"
            value={diasDesdeCompra}
            onChange={(e) => setDiasDesdeCompra(e.target.value)}
            placeholder="ex: 3 (dentro janela) ou 30 (fora janela)"
            min={0}
          />
          <div className="flex gap-2 mt-1.5 flex-wrap">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setDiasDesdeCompra('2')}
            >
              Dentro 7 dias (2)
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setDiasDesdeCompra('30')}
            >
              Fora janela (30)
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setDiasDesdeCompra('')}
            >
              Sem compra
            </button>
          </div>
        </div>

        <div>
          <label className="label" htmlFor="pg-creditos">
            Créditos de tiragem
          </label>
          <input
            id="pg-creditos"
            type="number"
            className="input"
            value={creditos}
            onChange={(e) => setCreditos(e.target.value)}
            min={0}
          />
        </div>

        <Toggle
          label="Tem upsell aprovado (Mapa Astral)"
          checked={temUpsell}
          onChange={setTemUpsell}
        />
        <Toggle
          label="Tem acesso ao mapa (manual override)"
          checked={acessoMapa}
          onChange={setAcessoMapa}
        />
        <Toggle
          label="Validada (pula token de validação)"
          checked={validada}
          onChange={setValidada}
        />
      </div>

      <button
        type="button"
        className="btn-primary w-full"
        onClick={() => apply.mutate()}
        disabled={apply.isPending}
        aria-busy={apply.isPending}
      >
        {apply.isPending ? 'Aplicando…' : 'Aplicar config'}
      </button>

      {apply.isError && (
        <p role="alert" className="text-sara-danger text-xs">
          {apply.error instanceof ApiError ? apply.error.message : 'Erro ao aplicar.'}
        </p>
      )}

      <hr className="border-sara-border" />

      <details className="text-xs muted">
        <summary className="cursor-pointer font-medium text-sara-text">Estado atual</summary>
        <dl className="mt-2 space-y-1">
          <div className="flex justify-between">
            <dt>Créditos:</dt>
            <dd>{state.cliente.creditosTiragem}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Primeira compra:</dt>
            <dd>
              {state.cliente.diasDesdeCompra !== null
                ? `${state.cliente.diasDesdeCompra}d atrás`
                : '—'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt>Pergunta pendente:</dt>
            <dd
              className="truncate max-w-[180px]"
              title={state.cliente.perguntaTiragemPendente ?? ''}
            >
              {state.cliente.perguntaTiragemPendente ?? '—'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt>Compras:</dt>
            <dd>{state.compras.length}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Validada:</dt>
            <dd>{state.conversa.validada ? 'sim' : 'não'}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Pausada:</dt>
            <dd>{state.conversa.pausada ? 'sim' : 'não'}</dd>
          </div>
        </dl>
      </details>
    </aside>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-sara-border bg-sara-bg p-3">
      <p className="text-sm text-sara-text flex-1">{label}</p>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className="toggle"
        data-state={checked ? 'on' : 'off'}
      >
        <span className="toggle-thumb" aria-hidden="true" />
      </button>
    </div>
  );
}
